import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api, useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';

const VERDICT_STYLES = {
  custom_good:    'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  custom_basic:   'bg-amber-50  text-amber-700  dark:bg-amber-950/40 dark:text-amber-300',
  custom_minimal: 'bg-amber-50  text-amber-700  dark:bg-amber-950/40 dark:text-amber-300',
  generic:        'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
  soft_404:       'bg-red-50    text-red-700    dark:bg-red-950/40    dark:text-red-300',
  redirect:       'bg-red-50    text-red-700    dark:bg-red-950/40    dark:text-red-300',
  unexpected:     'bg-red-50    text-red-700    dark:bg-red-950/40    dark:text-red-300',
};

export default function SiteAuditPage() {
  const { user } = useAuth();
  const { selectedProjectId, selectedProject } = useProject();
  // One account = one site: clients may only audit their own project's domain,
  // so we prefill + lock the URL for them. Staff can audit any site.
  const isClient = user?.role === 'client';
  const projectDomain = selectedProject?.domain || '';
  const [activeTab, setActiveTab] = useState('404');
  const [url, setUrl] = useState('');
  const [maxPages, setMaxPages] = useState(100);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // For clients, keep the target URL pinned to their own site.
  useEffect(() => {
    if (isClient && projectDomain) setUrl(projectDomain);
  }, [isClient, projectDomain]);

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const run = async (e) => {
    e.preventDefault();
    setResult(null);
    setLoading(true);
    setStatus('Running audit...');
    try {
      const endpoint = {
        '404': '/site-audit/404-check',
        'nav': '/site-audit/navigation',
        'permalinks': '/site-audit/permalinks',
      }[activeTab];
      // Clients are locked to their own site regardless of the input value.
      const target = isClient ? projectDomain : url;
      const body = activeTab === 'permalinks'
        ? { url: target, max_pages: maxPages, project_id: selectedProjectId }
        : { url: target, project_id: selectedProjectId };
      const { data } = await api.post(endpoint, body);
      setResult(data);
      setStatus('Audit complete!');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const Recommendations = ({ items }) => (
    items?.length ? (
      <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 dark:text-slate-300">
        {items.map((r, i) => <li key={i}>{r}</li>)}
      </ul>
    ) : <p className="text-sm text-emerald-600">No issues found.</p>
  );

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Site Audit</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Check 404 pages, navigation structure, and permalink quality.</p>
      </div>

      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {[
          { id: '404', label: '404 Page' },
          { id: 'nav', label: 'Navigation' },
          { id: 'permalinks', label: 'Permalinks' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-1 text-sm ${activeTab === t.id ? 'border-b-2 border-violet-600 font-medium text-violet-600' : 'text-slate-500'}`}
            onClick={() => { setActiveTab(t.id); setResult(null); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={run} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Target URL</label>
          {isClient ? (
            projectDomain ? (
              <div className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-sm text-slate-600 dark:text-slate-300">
                {projectDomain}
                <span className="ml-2 text-xs text-slate-400">(your site — audits are locked to your account)</span>
              </div>
            ) : (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                No website is set for your project yet. Ask your account manager to add it before running an audit.
              </p>
            )
          ) : (
            <input
              type="url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com" required
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            />
          )}
        </div>
        {activeTab === 'permalinks' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Max Pages ({maxPages})</label>
            <input type="range" min="20" max="200" step="10" value={maxPages} onChange={(e) => setMaxPages(parseInt(e.target.value))} className="w-full" />
          </div>
        )}
        <button type="submit" disabled={loading || (isClient && !projectDomain)} className="bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60">
          {loading ? 'Auditing...' : 'Run Audit'}
        </button>
        {status && (
          <div className={`p-3 rounded-md text-sm ${status.startsWith('Error') ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'}`}>
            {status}
          </div>
        )}
      </form>

      {result && activeTab === '404' && result.success && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
          <div className={`inline-flex px-2 py-1 rounded text-xs font-medium ${VERDICT_STYLES[result.verdict] || 'bg-slate-100 text-slate-700'}`}>
            {result.verdict.replace('_', ' ')}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Status Code:</span> <span className="font-medium">{result.status_code}</span></div>
            <div><span className="text-slate-500">Returns 404:</span> <span className="font-medium">{result.returns_404 ? 'Yes' : 'No'}</span></div>
            <div><span className="text-slate-500">Custom Page:</span> <span className="font-medium">{result.has_custom_page ? 'Yes' : 'No'}</span></div>
            <div><span className="text-slate-500">Has Home Link:</span> <span className="font-medium">{result.has_home_link ? 'Yes' : 'No'}</span></div>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">{result.reason}</p>
          <div>
            <h3 className="text-sm font-semibold mb-2">Recommendations</h3>
            <Recommendations items={result.recommendations} />
          </div>
        </div>
      )}

      {result && activeTab === 'nav' && result.success && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Score:</span> <span className="font-medium">{result.score}/100</span></div>
            <div><span className="text-slate-500">Found Nav:</span> <span className="font-medium">{result.found_nav ? 'Yes' : 'No'}</span></div>
            {result.found_nav && (
              <>
                <div><span className="text-slate-500">Items:</span> <span className="font-medium">{result.item_count}</span></div>
                <div><span className="text-slate-500">External Links:</span> <span className="font-medium">{result.external_count}</span></div>
              </>
            )}
          </div>
          {result.items?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Nav Items</h3>
              <ul className="text-sm space-y-1 max-h-64 overflow-auto">
                {result.items.map((i, idx) => (
                  <li key={idx} className="text-slate-700 dark:text-slate-300">
                    <span className="font-medium">{i.label || '(empty)'}</span>
                    <span className="text-slate-500"> → {i.href}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <h3 className="text-sm font-semibold mb-2">Recommendations</h3>
            <Recommendations items={result.recommendations} />
          </div>
        </div>
      )}

      {result && activeTab === 'permalinks' && result.success && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Score:</span> <span className="font-medium">{result.score}/100</span></div>
            <div><span className="text-slate-500">Pages:</span> <span className="font-medium">{result.pages_audited}</span></div>
            <div><span className="text-slate-500">Clean URLs:</span> <span className="font-medium">{result.clean_urls}</span></div>
            <div><span className="text-slate-500">Avg Length:</span> <span className="font-medium">{result.avg_length}</span></div>
          </div>
          {result.flag_counts && Object.keys(result.flag_counts).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Issue Counts</h3>
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(result.flag_counts).map(([k, v]) => (
                  <span key={k} className="px-2 py-1 rounded bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">{k}: {v}</span>
                ))}
              </div>
            </div>
          )}
          {result.samples?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Sample Flagged URLs</h3>
              <ul className="text-xs space-y-1 max-h-64 overflow-auto font-mono">
                {result.samples.map((s, idx) => (
                  <li key={idx} className="text-slate-700 dark:text-slate-300">
                    {s.url} <span className="text-amber-600">[{s.flags.join(', ')}]</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <h3 className="text-sm font-semibold mb-2">Recommendations</h3>
            <Recommendations items={result.recommendations} />
          </div>
        </div>
      )}
    </motion.div>
  );
}
